import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useState } from "react";
import departmentsService from "services/departments-service";
import DepartmentDialog from "./DepartmentDialog.js";

export default function AllDepartments({ permissions }) {
  const [departments, setDepartments] = useState(null);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const { label } = useLanguage();
  const [rows, setRows] = useState([]);

  const [newDialogIsOpen, setNewDialogIsOpen] = useState(false);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);

  const columns = [
    {
      Header: label("name-singular"),
      accessor: "description",
      width: "100%",
    },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }

  useEffect(() => {
    updateRows();
  }, [departments]);

  async function handleAddDepartment(newElement) {
    const [response, data] =
      await departmentsService.createDepartment(newElement);
    if (response.ok) {
      setDepartments(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateDepartment(newElement) {
    const [response, data] = await departmentsService.updateDepartment(
      newElement.id,
      newElement
    );
    if (response.ok) {
      setDepartments(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteDepartment(id) {
    const [response, data] = await departmentsService.removeDepartment(id);
    if (response.ok) {
      setDepartments(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  function updateRows() {
    setRows(
      departments?.length > 0
        ? departments.map((department) => {
            return {
              description: (
                <MDTypography variant="inherit">
                  {department.description}
                </MDTypography>
              ),

              actions: (
                <MDBox
                  display="flex"
                  flexDirection="row"
                  justifyContent="end"
                  alignItems="center"
                >
                  {permissions.update && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditDialogIsOpen(Math.random());
                        setCurrentDepartment(department);
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  )}
                  {permissions.delete && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAlertIsOpen(Math.random());
                        setCurrentDepartment(department);
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  )}
                </MDBox>
              ),
            };
          })
        : departments?.length === 0
          ? [
              {
                description: label("no-departments-were-found"),
              },
            ]
          : [
              {
                description: label("loading"),
              },
            ]
    );
  }

  return (
    <>
      <AlertDialog
        open={alertIsOpen}
        setOpen={setAlertIsOpen}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeleteDepartment(currentDepartment.id);
        }}
      />
      <DepartmentDialog
        isOpen={newDialogIsOpen}
        onSubmit={handleAddDepartment}
      />
      <DepartmentDialog
        type="edit"
        isOpen={editDialogIsOpen}
        onSubmit={handleUpdateDepartment}
        data={currentDepartment}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("department-plural")}{" "}
                  {permissions.get ? `(${departments?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      setNewDialogIsOpen(Math.random());
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-department-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-departments"
                      internalLoading={!Boolean(departments)}
                      fullWidth
                      setData={setDepartments}
                      onSearch={async (request, callback) => {
                        callback(
                          await departmentsService.getDepartments(request)
                        );
                      }}
                      label={label("search-departments-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(departments)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
